import SessionResource from 'flow/api/session';
import SocialIntegrationResource from 'flow/api/socialintegration';

import { fromJS, List, Map } from 'immutable';
import Actions from 'flow/actions';
import {
    httpGetErrorDispatcher,
    networkErrorDispatcher,
    setAutoLoginWithAccessToken,
    updateApplicationState,
} from 'flow/actions/app-actions';
import { history } from 'flow/configure-store';
import { routes } from 'flow/types/routes';
import { listToMap } from 'flow/utils';
import { getSingleSignOnBrandingKey } from 'flow/utils/brand-utils';
import { hasNetworkConnection, setAutoLogin } from 'flow/utils/browser-utils';
import { electronActions } from 'flow/utils/electron-utils';
import { generatePath } from 'flow/utils/router-utils';
import { goToExternalSignIn } from 'flow/utils/social-integration-utils';
import isElectron from 'is-electron';
import { TEAMS_AUTH_FLOW_START } from 'flow/constants/teams-auth-constants';
import { SYNC_SOURCE } from 'flow/utils/constants/app-constants';
import { addGeneralLogEntry } from 'flow/utils/browser-utils';
import {
    WEB_DESKTOP_DOMAIN_KEY,
    WEB_DESKTOP_TOKEN_EXPIRATION_IN_MINUTES,
} from 'flow/utils/constants/app-constants';
import { createIntegrationAccessTokenForLoggedInUser } from './integration-access-actions';

let settingsPollTimeout = null;
let signInPollTimeout = null;

export const waitForSocialIntegrationSettings =
    (extensionKey, syncSource) => (dispatch) => {
        if (settingsPollTimeout) {
            clearTimeout(settingsPollTimeout);
        }
        settingsPollTimeout = setTimeout(
            () =>
                dispatch(
                    checkSocialIntegrationSettings(extensionKey, syncSource),
                ),
            1000,
        );
    };

const checkSocialIntegrationSettings =
    (extensionKey, syncSource) => (dispatch) => {
        SocialIntegrationResource.getSocialIntegrationSettings({
            onResult(json) {
                const data = listToMap(fromJS(json), (e) =>
                    e.get('syncSource'),
                );
                if (data.getIn([syncSource, 'authInProgress'])) {
                    dispatch(
                        waitForSocialIntegrationSettings(
                            extensionKey,
                            syncSource,
                        ),
                    );
                } else {
                    dispatch(receiveSocialIntegrationSettings(json));

                    history.push(
                        generatePath(
                            routes[
                                'settings.account.socialintegration.integration.callback'
                            ],
                            {
                                extensionKey,
                                syncSource,
                            },
                        ),
                    );
                }
            },
        });
    };

export const stopSettingsPollTimeout = () => {
    if (settingsPollTimeout) {
        clearTimeout(settingsPollTimeout);
        settingsPollTimeout = null;
    }
};

export const fetchSocialIntegrationSettings = () => (dispatch) => {
    return new Promise((resolve, reject) => {
        SocialIntegrationResource.getSocialIntegrationSettings({
            onResult(json) {
                dispatch(receiveSocialIntegrationSettings(json));
                resolve();
            },
            onError: (error) => {
                dispatch(receiveSocialIntegrationError(error));
                httpGetErrorDispatcher(dispatch)(error);
                reject(error);
            },
            onClientError: (error) => {
                reject(error);
            },
        });
    });
};

export const createNewAuthProcess =
    (syncDataSource, callbackUrl, entityKey) => (dispatch) => {
        dispatch(receiveSocialIntegrationAuthInProgress(syncDataSource));

        const startOauthSignInDTO = Map()
            .set('syncSource', syncDataSource)
            .set('syncTypes', List(['openid']))
            .set('callbackUrl', callbackUrl);

        SocialIntegrationResource.startOauthSignInProcess(
            {
                async onResult(url) {
                    await goToExternalSignIn(
                        dispatch,
                        url,
                        syncDataSource,
                        entityKey,
                    );
                },
                onError: networkErrorDispatcher(dispatch),
            },
            startOauthSignInDTO,
        );
    };

export const socialIntegrationSignOut = (syncDataSource) => (dispatch) => {
    return new Promise((resolve, reject) => {
        SocialIntegrationResource.signOut(
            {
                onResult(/*json*/) {
                    resolve();
                    // TODO Team-SI: Investigate what we should do in case of an unsuccessful sign out
                },
                onError: (error) => {
                    networkErrorDispatcher(dispatch)(error);
                    reject(error);
                },
                onClientError: (error) => {
                    reject(error);
                },
            },
            syncDataSource,
        );
    });
};

export const changeSocialIntegrationSettings =
    (syncSource, callbackUrl, newSyncTypes) => (dispatch) => {
        dispatch(receiveSocialIntegrationAuthInProgress(syncSource));

        const syncTypeChangeDTO = Map()
            .set('callbackUrl', callbackUrl)
            .set('syncTypes', newSyncTypes.syncTypes);

        return new Promise((resolve, reject) => {
            SocialIntegrationResource.setSyncTypes(
                {
                    onResult(result) {
                        resolve(result);
                    },
                    onError: (error) => {
                        httpGetErrorDispatcher(dispatch)(error);
                        reject(error);
                    },
                    onClientError: (error) => {
                        reject(error);
                    },
                },
                syncSource,
                syncTypeChangeDTO,
            );
        });
    };

export const startExternalSignIn =
    (syncSource, callbackUrl, appRegistrationId) => (dispatch) => {
        dispatch(
            receiveExternalSigninData({
                waiting: true,
                syncSource: syncSource,
            }),
        );

        SocialIntegrationResource.startExternalSignIn(
            {
                onResult(result) {
                    electronActions.startExternalSignIn(result, syncSource);
                },
                onError() {
                    const data = {
                        error: true,
                        syncSource: syncSource,
                        networkFail: !hasNetworkConnection(),
                        serverFail: hasNetworkConnection(),
                        waiting: false,
                    };
                    dispatch(receiveExternalSigninData(data));
                },
            },
            syncSource,
            appRegistrationId,
            getSingleSignOnBrandingKey(syncSource),
            null,
            callbackUrl,
        );
    };

export const waitForFinishExternalSignIn =
    (token, syncSource) => (dispatch) => {
        signInPollTimeout = setTimeout(
            () => dispatch(finishExternalSignIn(token, syncSource)),
            1000,
        );
    };

/**
 * "Sign in with Microsoft" inside a Teams tab. The tab can't follow the normal
 * redirect sign-in, so Teams opens it in a popup (TeamsAuthenticationPopup) and
 * hands the token back; from there it finishes like any other external sign-in.
 * Unlike silent SSO this doesn't depend on the tab's domain matching the Entra
 * app, so it works where silent SSO doesn't (INTE-5063).
 */
export const startTeamsMicrosoftSignIn = () => (dispatch) =>
    // teams-js is loaded here, not at the top: this module is imported almost
    // everywhere, and teams-js needs browser APIs (fetch) as soon as it loads.
    import('@microsoft/teams-js')
        .then((microsoftTeams) =>
            microsoftTeams.authentication.authenticate({
                url: `${window.location.origin}${TEAMS_AUTH_FLOW_START}`,
                width: 800,
                height: 600,
            }),
        )
        .then((token) =>
            dispatch(finishExternalSignIn(token, SYNC_SOURCE.microsoft)),
        )
        .catch((error) => {
            // Closing the popup is the user's choice, not an error to show.
            if (`${error?.message ?? error}`.includes('CancelledByUser')) {
                return;
            }
            addGeneralLogEntry('Teams Microsoft sign-in failed:', error);
            dispatch(
                receiveExternalSigninData({
                    error: true,
                    syncSource: SYNC_SOURCE.microsoft,
                    networkFail: !hasNetworkConnection(),
                    serverFail: hasNetworkConnection(),
                    waiting: false,
                }),
            );
        });

const finishExternalSignIn = (token, syncSource) => (dispatch) => {
    SocialIntegrationResource.finishExternalSignIn(
        {
            onResult(result) {
                if (result.signInStatus === 'ok') {
                    dispatch(fetchSession());
                } else if (result.signInStatus === 'ongoing') {
                    dispatch(waitForFinishExternalSignIn(token, syncSource));
                }
            },
            onError() {
                const data = {
                    error: true,
                    syncSource: syncSource,
                    networkFail: !hasNetworkConnection(),
                    serverFail: hasNetworkConnection(),
                    waiting: false,
                };
                dispatch(receiveExternalSigninData(data));
            },
            onClientError() {
                const data = {
                    error: true,
                    syncSource: syncSource,
                    networkFail: !hasNetworkConnection(),
                    userEmailFail: hasNetworkConnection(),
                    waiting: false,
                };
                dispatch(receiveExternalSigninData(data));
            },
        },
        token,
        syncSource,
    );
};

const saveRememberMeIntegrationAccessToken = (dispatch) => (tokenData) => {
    if (!tokenData?.accessToken) return;
    setAutoLogin(true);
    electronActions.saveIntegrationToken(tokenData);

    dispatch(setAutoLoginWithAccessToken(tokenData?.key));
};

const fetchSession = () => (dispatch) => {
    SessionResource.getLoggedInSession({
        onResult(/*json*/) {
            dispatch(updateApplicationState('boot'));

            if (isElectron()) {
                /**
                 * Creates an integration access token with 30-day expiration
                 * for remember-me login process with social login
                 */
                dispatch(
                    createIntegrationAccessTokenForLoggedInUser(
                        WEB_DESKTOP_DOMAIN_KEY,
                        'DesktopApp',
                        WEB_DESKTOP_TOKEN_EXPIRATION_IN_MINUTES,
                        saveRememberMeIntegrationAccessToken(dispatch),
                    ),
                );
                electronActions.deleteSavedUserName();
            }

            dispatch(
                receiveExternalSigninData({
                    error: false,
                }),
            );
            history.push('/');
        },
        onError(/*status*/) {
            const data = {
                error: true,
                networkFail: !hasNetworkConnection(),
                serverFail: hasNetworkConnection(),
                waiting: false,
            };
            dispatch(receiveExternalSigninData(data));
        },
    });
};

export const cancelExternalSignIn = () => (dispatch) => {
    clearTimeout(signInPollTimeout);
    dispatch(
        receiveExternalSigninData({
            waiting: false,
            error: false,
        }),
    );
};

export const fetchCloudFiles = (chatSessionKey) => (dispatch) => {
    dispatch(clearCloudFilesData());
    return getCloudFileIntegrationActive()
        .then((active) => {
            if (active) {
                return new Promise((resolve) => {
                    SocialIntegrationResource.getCloudFilesForChatSession(
                        {
                            onResult(json) {
                                const cloudFilesData = {
                                    chatSessionKey: chatSessionKey,
                                    files: fromJS(json),
                                    status: {
                                        active: true,
                                        error: false,
                                    },
                                };
                                dispatch(receiveCloudFilesData(cloudFilesData));
                                resolve();
                            },
                        },
                        chatSessionKey,
                    );
                });
            } else {
                return new Promise((resolve) => {
                    receiveInactiveCloudFileStatus(dispatch);
                    resolve();
                });
            }
        })
        .catch(() => {
            return new Promise((resolve) => {
                receiveErrorCloudFileStatus(dispatch);
                resolve();
            });
        });
};

const getCloudFileIntegrationActive = () => {
    return new Promise((resolve, reject) => {
        SocialIntegrationResource.getSocialIntegrationSettings({
            onResult(json) {
                resolve(json);
            },
            onError() {
                reject();
            },
            onClientError() {
                reject();
            },
        });
    })
        .then((json) => {
            const socialIntegrationSettings = listToMap(fromJS(json), (e) =>
                e.get('syncSource'),
            );
            return socialIntegrationSettings
                .getIn(['google', 'syncTypes'], [])
                .includes('cloud_files');
        })
        .catch(() => false);
};

const receiveInactiveCloudFileStatus = (dispatch) => {
    const cloudFilesStatus = {
        error: false,
        active: false,
    };
    dispatch(receiveCloudFilesStatus(cloudFilesStatus));
};

const receiveErrorCloudFileStatus = (dispatch) => {
    const cloudFilesStatus = {
        error: true,
        active: true,
    };
    dispatch(receiveCloudFilesStatus(cloudFilesStatus));
};

const receiveExternalSigninData = (data) => ({
    type: Actions.APP_EXTERNAL_SIGNIN_DATA,
    data: data,
});

const receiveSocialIntegrationSettings = (json) => {
    return {
        type: Actions.RESULT_SOCIAL_INTEGRATION_SETTINGS,
        data: json,
    };
};

const receiveSocialIntegrationAuthInProgress = (syncSource) => {
    return {
        type: Actions.RESULT_SOCIAL_INTEGRATION_AUTH_IN_PROGRESS,
        data: syncSource,
    };
};

const receiveSocialIntegrationError = (json) => {
    return {
        type: Actions.RESULT_SOCIAL_INTEGRATION_ERROR,
        data: json,
    };
};

const receiveCloudFilesData = (data) => ({
    type: Actions.RESULT_SOCIAL_INTEGRATION_CLOUD_FILES,
    data: data,
});

const receiveCloudFilesStatus = (data) => ({
    type: Actions.RESULT_SOCIAL_INTEGRATION_CLOUD_FILES_STATUS,
    data: data,
});

const clearCloudFilesData = () => ({
    type: Actions.RESULT_SOCIAL_INTEGRATION_CLOUD_FILES_CLEAR,
});

export const externalSyncIsDone = (syncIsDone) => ({
    type: Actions.RESULT_SOCIAL_INTEGRATION_EXTERNAL_SYNC,
    syncIsDone: syncIsDone,
});
